import { Injectable } from '@angular/core';
import { Contact } from '../models/conflict-result';
import { CitizenshipCode, CountryFieldRequirement, RequirementLevel } from '../models/country-field-requirement';

export interface FieldValidationFailure {
    field: string;
    message: string;
}

export interface FieldValidationResult {
    isValid: boolean;
    failures: FieldValidationFailure[];
}

const REQUIRED: RequirementLevel = 'Required';

@Injectable({
    providedIn: 'root'
})
export class CountryFieldValidationService {

    // countryConfigMap is keyed by upper-cased ISO alpha-2 country code. enforcementEnabled
    // mirrors the backend's own kill switch (CountryFieldRequirementSettings.EnforcementEnabled) -
    // when disabled, this falls back to the same permissive legacy checks used for an
    // unconfigured country, so a backend-side toggle to unblock live matters isn't undermined
    // by the frontend still blocking on the country-specific rules.
    validate(contact: Contact, countryConfigMap: Map<string, CountryFieldRequirement>, enforcementEnabled: boolean = true, citizenshipCodes: CitizenshipCode[] = []): FieldValidationResult {
        if (!enforcementEnabled) {
            return this.validateLegacy(contact);
        }

        if (contact.isCompany) {
            return this.validateLegacy(contact);
        }

        // Australia is explicitly excluded, mirroring the backend's own IsAustralia() guard -
        // even if AU is present and active in countryConfigMap, it's bypassed here unconditionally.
        if (this.isAustralia(contact.country)) {
            return this.validateLegacy(contact);
        }

        const config = countryConfigMap.get((contact.country ?? '').toUpperCase());

        if (!config) {
            return this.validateLegacy(contact);
        }

        return this.validateAgainstCountryConfig(contact, config, citizenshipCodes);
    }

    private isAustralia(country: string | null): boolean {
        return (country ?? '').toUpperCase() === 'AU';
    }

    // Port of contact-result.component.ts's pre-existing checkValidations() field list.
    private validateLegacy(contact: Contact): FieldValidationResult {
        const failures: FieldValidationFailure[] = [];

        if (!contact.isCompany && !contact.firstName) {
            failures.push({ field: 'FirstName', message: 'First name' });
        }

        if (!contact.postcode) {
            failures.push({ field: 'Postcode', message: 'Zip Postcode' });
        }

        if (!contact.country) {
            failures.push({ field: 'Country', message: 'Country' });
        }

        if (contact.isCompany && !contact.businessRegNumber) {
            failures.push({ field: 'BusinessRegNumber', message: 'Business Reg. Number' });
        }

        if (!contact.isCompany && !contact.dateOfBirth) {
            failures.push({ field: 'DateOfBirth', message: 'Date of Birth' });
        }

        return { isValid: failures.length === 0, failures };
    }

    // Country-driven Required-tier checks. Recommended/Optional/NotApplicable never block.
    private validateAgainstCountryConfig(contact: Contact, config: CountryFieldRequirement, citizenshipCodes: CitizenshipCode[]): FieldValidationResult {
        const failures: FieldValidationFailure[] = [];

        if (config.nameRequirement === REQUIRED && !contact.firstName && !contact.lastName) {
            failures.push({ field: 'Name', message: `Name is required for ${config.countryName}` });
        }

        if (config.addressRequirement === REQUIRED && !contact.street && !contact.postcode) {
            failures.push({ field: 'Address', message: `Address is required for ${config.countryName}` });
        }

        if (config.dateOfBirthRequirement === REQUIRED && !contact.dateOfBirth) {
            failures.push({ field: 'DateOfBirth', message: `Date of birth is required for ${config.countryName}` });
        }

        if (config.phoneRequirement === REQUIRED && !contact.phone && !contact.mobilePhoneNumber) {
            failures.push({ field: 'Phone', message: `Phone is required for ${config.countryName}` });
        }

        if (config.nationalIdRequirement === REQUIRED && !contact.taxNumber) {
            failures.push({ field: 'NationalId', message: `National ID is required for ${config.countryName}` });
        }

        if (config.requiresUaeCitizenship) {
            if (!contact.citizenship) {
                failures.push({ field: 'Citizenship', message: `Citizenship is required for ${config.countryName}` });
            } else if (!citizenshipCodes.some(code => code.apiInputCode === contact.citizenship)) {
                // Blocks a value that doesn't match any of the LSEG-provided citizenship codes -
                // e.g. free-typed text that was never actually selected from the dropdown - since
                // the isCitizenshipInvalid display warning on its own never prevented submission.
                failures.push({ field: 'Citizenship', message: `Select a valid citizenship from the list for ${config.countryName}` });
            }
        }

        const selectedDocumentType = contact.selectedDocuments?.[0]?.selectedOption;

        // Mirrors the backend: the version is only required when Driving Licence is the selected
        // document type and a number is actually being sent - any other New Zealand document type
        // (e.g. representing National ID) doesn't need it.
        if (config.requiresNewZealandVersion && selectedDocumentType === 'DrivingLicence' && contact.taxNumber && !contact.drivingLicenceVersion) {
            failures.push({ field: 'DrivingLicenceVersion', message: `Driving licence version is required for ${config.countryName}` });
        }

        // Mirrors the backend: the issue date is only required when National ID is the selected
        // document type and a National ID number is actually being sent - Tanzania's other
        // document types (Driving Licence, TIN, etc.) never need it.
        if (config.requiresTanzaniaIssueDate && selectedDocumentType === 'NationalId' && contact.taxNumber && !contact.nationalIdIssueDate) {
            failures.push({ field: 'NationalIdIssueDate', message: `National ID issue date is required for ${config.countryName}` });
        }

        return { isValid: failures.length === 0, failures };
    }
}
